import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { realtime } from '@shared/realtime/ws';
import { Loader } from '@shared/ui/Progress';
import {
  useConcludeContractMutation,
  useDeleteContractMutation,
  useGetContractDetailsQuery,
  useGetContractCorrespondenceQuery,
  useListStatutProjectsQuery,
  useAddManualContractRiskMutation,
  usePatchContractRiskMutation,
  useDeleteContractRiskMutation,
  useBulkAnnotateContractRisksMutation,
  useComposeContractProtocolMutation,
  useListComposedForContractQuery,
  useDeleteComposedMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import {
  protocolGenerationStarted,
  protocolGenerationFinished,
} from '@features/analysisProgress/model/slice';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { ManualRiskModal } from './ManualRiskModal';
import { DispatchRisksModal } from './DispatchRisksModal';
import { RiskClausePanel } from './RiskClausePanel';
import { RiskBulkActionBar } from './RiskBulkActionBar';
import { ExternalAuditModal } from './ExternalAuditModal';
import { ContractChatModal } from './ContractChatModal';
import { CounterpartyCard } from './CounterpartyCard';
import { ContractHeaderSection } from './ContractCard/ContractHeaderSection';
import { ContractVersionsSection } from './ContractCard/ContractVersionsSection';
import { ContractRisksSection } from './ContractCard/ContractRisksSection';
import { ContractDeadlinesSection } from './ContractCard/ContractDeadlinesSection';
import { CATEGORY_LABELS } from './ContractCard/styles';

interface Props {
  contractId: string;
  onClose: () => void;
}

export function ContractCard({ contractId, onClose }: Props) {
  const { can_write } = useAgentPermission('statut');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((s) => s.auth.accessToken);

  const { data, isLoading } = useGetContractDetailsQuery(contractId, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: linkedCorrespondence } = useGetContractCorrespondenceQuery(contractId);
  const { data: projectsData } = useListStatutProjectsQuery();
  const { setActiveProject } = useActiveProjectContext();

  const [deleteContract, { isLoading: isDeleting }] = useDeleteContractMutation();
  const [concludeContract, { isLoading: isConcluding }] = useConcludeContractMutation();
  const [concludeError, setConcludeError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Sync active project context
  useEffect(() => {
    const pid = data?.contract.project_id;
    if (!pid) return;
    const proj = projectsData?.items.find((p) => p.id === pid);
    setActiveProject(pid, { name: proj?.name, code: proj?.code });
  }, [data?.contract.project_id, projectsData?.items, setActiveProject]);

  // Collapsed versions state
  const [collapsedVersions, setCollapsedVersions] = useState<Record<string, boolean>>({});
  function toggleVersion(versionId: string) {
    setCollapsedVersions((prev) => ({
      ...prev,
      [versionId]: !prev[versionId],
    }));
  }

  // Stage G: Protocol composition & management
  const [composeProtocol, { isLoading: isQueuingProtocol }] = useComposeContractProtocolMutation();
  const { data: composedProtocolsData } = useListComposedForContractQuery(contractId);
  const [deleteComposed] = useDeleteComposedMutation();
  const composedProtocols = composedProtocolsData?.items ?? [];

  const isGeneratingProtocol = useAppSelector((s) =>
    s.analysisProgress.generatingProtocols.includes(contractId),
  );
  const [protocolFallback, setProtocolFallback] = useState<string | null>(null);

  useEffect(() => {
    const unsub = realtime.subscribe((event) => {
      if (event.event === 'contract_protocol_composed' && event.contract_id === contractId) {
        if (event.fallback) {
          setProtocolFallback(
            event.reason ?? 'Не удалось собрать DOCX-протокол по шаблону (причина не указана).',
          );
        } else {
          setProtocolFallback(null);
        }
      }
    });
    return unsub;
  }, [contractId]);

  async function onComposeProtocol() {
    setProtocolFallback(null);
    try {
      dispatch(protocolGenerationStarted(contractId));
      await composeProtocol(contractId).unwrap();
    } catch (err) {
      dispatch(protocolGenerationFinished(contractId));
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Не удалось поставить задачу в очередь.';
      setProtocolFallback(msg);
    }
  }

  async function onDownloadComposedProtocol(composedId: string) {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    const res = await downloadAuthorizedFile(`${base}/agents/statut/composed/${composedId}/download`, {
      accessToken,
      fallbackName: `Протокол-разногласий-${contractId}.docx`,
    });
    if (!res.ok) {
      setProtocolFallback(`Не удалось скачать DOCX (${res.error}).`);
    }
  }

  async function onDeleteComposedProtocol(composedId: string) {
    if (!window.confirm('Удалить этот сгенерированный протокол разногласий? Действие необратимо.')) {
      return;
    }
    try {
      await deleteComposed(composedId).unwrap();
    } catch {
      setProtocolFallback('Не удалось удалить DOCX. Попробуйте ещё раз.');
    }
  }

  // Modals state
  const [auditOpen, setAuditOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [counterpartyCardId, setCounterpartyCardId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [manualRiskOpen, setManualRiskOpen] = useState(false);
  const [dispatchRisksOpen, setDispatchRisksOpen] = useState(false);
  const [editingRiskId, setEditingRiskId] = useState<string | null>(null);
  const [deletingRiskId, setDeletingRiskId] = useState<string | null>(null);
  const [viewClauseRiskId, setViewClauseRiskId] = useState<string | null>(null);

  // Selection & batch actions
  const [selectMode, setSelectMode] = useState(false);
  const [selectedRisks, setSelectedRisks] = useState<Set<string>>(new Set());
  const [addManualRisk] = useAddManualContractRiskMutation();
  const [patchRisk] = usePatchContractRiskMutation();
  const [deleteRisk] = useDeleteContractRiskMutation();
  const [bulkAnnotate, { isLoading: isBulkAnnotating }] = useBulkAnnotateContractRisksMutation();

  function toggleRiskSelection(id: string) {
    setSelectedRisks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedRisks(new Set());
  }

  async function bulkDecide(decision: 'accepted' | 'disputed' | 'rejected') {
    if (selectedRisks.size === 0) return;
    await bulkAnnotate({
      contractId,
      riskIds: Array.from(selectedRisks),
      decision,
    }).unwrap();
    exitSelectMode();
  }

  async function onConclude() {
    if (
      !window.confirm(
        'Отметить договор как заключённым? В реестре «Договоры» появится новая ' +
          'карточка — туда нужно будет загрузить подписанный скан договора.',
      )
    ) {
      return;
    }
    setConcludeError(null);
    try {
      const { contract } = await concludeContract(contractId).unwrap();
      navigate(`/agents/statut/contracts/${contract.id}`);
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось отметить договор заключённым. Попробуйте ещё раз.';
      setConcludeError(msg);
    }
  }

  async function onDownloadConclusion() {
    setIsDownloading(true);
    try {
      const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
      const res = await downloadAuthorizedFile(`${base}/agents/statut/contracts/${contractId}/conclusion`, {
        accessToken,
        fallbackName: `Заключение-${contractId}.docx`,
      });
      if (!res.ok) throw new Error(res.error ?? `HTTP ${res.status}`);
    } finally {
      setIsDownloading(false);
    }
  }

  async function onDelete() {
    await deleteContract(contractId).unwrap();
    setConfirmOpen(false);
    onClose();
  }

  const meta = (data?.contract.metadata ?? {}) as Record<string, unknown>;
  const templateDeviations = meta.template_deviations as
    | Array<{ clause: string; description: string; template_says?: string | null; severity?: string | null }>
    | undefined;

  return (
    <>
      {isLoading || !data ? (
        <Loader />
      ) : (
        <>
          <ContractHeaderSection
            contractId={contractId}
            data={data}
            can_write={can_write}
            onOpenCounterparty={setCounterpartyCardId}
            onOpenChat={() => setChatOpen(true)}
            onDownloadConclusion={onDownloadConclusion}
            onConclude={onConclude}
            isDownloading={isDownloading}
            isConcluding={isConcluding}
            concludeError={concludeError}
          />

          <ContractVersionsSection
            contractId={contractId}
            contractStatus={data.contract.status}
            versions={data.versions}
            previousRisksCount={data.risks.length}
            can_write={can_write}
          />

          <ContractRisksSection
            contractId={contractId}
            contractStatus={data.contract.status}
            versions={data.versions}
            risks={data.risks}
            templateDeviations={templateDeviations}
            composedProtocols={composedProtocols}
            can_write={can_write}
            collapsedVersions={collapsedVersions}
            onToggleVersion={toggleVersion}
            selectMode={selectMode}
            onEnterSelectMode={() => setSelectMode(true)}
            selectedRisks={selectedRisks}
            onToggleRiskSelection={toggleRiskSelection}
            onOpenManualRisk={() => setManualRiskOpen(true)}
            onOpenDispatchRisks={() => setDispatchRisksOpen(true)}
            onComposeProtocol={onComposeProtocol}
            isQueuingProtocol={isQueuingProtocol}
            isGeneratingProtocol={isGeneratingProtocol}
            protocolFallback={protocolFallback}
            onDismissProtocolFallback={() => setProtocolFallback(null)}
            onDownloadComposedProtocol={onDownloadComposedProtocol}
            onDeleteComposedProtocol={onDeleteComposedProtocol}
            onViewClause={setViewClauseRiskId}
            onEditRisk={setEditingRiskId}
            onDeleteRisk={setDeletingRiskId}
          />

          <ContractDeadlinesSection
            contractId={contractId}
            contractStatus={data.contract.status}
            claims={data.claims}
            correspondence={linkedCorrespondence}
            deadlines={data.deadlines}
            executionTerms={data.execution_terms}
            can_write={can_write}
            onOpenCounterparty={setCounterpartyCardId}
            onOpenAudit={() => setAuditOpen(true)}
            onOpenConfirmDelete={() => setConfirmOpen(true)}
          />
        </>
      )}

      <ExternalAuditModal
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        scope="contract"
        entityId={contractId}
        entityLabel="Договор"
        contractStatus={data?.contract.status}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Удалить договор?"
        warning={`Договор с «${data?.contract.counterparty ?? ''}» будет полностью удалён вместе со всеми версиями и LLM-анализом рисков. Связанные претензии и иски сохранятся, но потеряют ссылку на этот договор. Действие необратимо.`}
        confirmText={data?.contract.counterparty ?? ''}
        confirmLabel="Введите название контрагента для подтверждения"
        confirmButtonLabel="Удалить безвозвратно"
        busyLabel="Удаляем…"
        isBusy={isDeleting}
        onConfirm={onDelete}
        onCancel={() => setConfirmOpen(false)}
      />
      <ContractChatModal
        open={chatOpen}
        contractId={contractId}
        contractTitle={data?.contract.counterparty ?? ''}
        onClose={() => setChatOpen(false)}
      />
      {counterpartyCardId && (
        <CounterpartyCard counterpartyId={counterpartyCardId} onClose={() => setCounterpartyCardId(null)} />
      )}
      <ManualRiskModal
        open={manualRiskOpen}
        mode="add"
        scope="contract"
        onClose={() => setManualRiskOpen(false)}
        onSubmit={async (v) => {
          await addManualRisk({
            contractId,
            category: v.category,
            severity: v.severity,
            description: v.description,
            proposed_change: v.proposed,
          }).unwrap();
        }}
      />
      <DispatchRisksModal contractId={contractId} open={dispatchRisksOpen} onClose={() => setDispatchRisksOpen(false)} />
      {editingRiskId &&
        (() => {
          const r = data?.risks.find((x) => x.id === editingRiskId);
          if (!r) return null;
          return (
            <ManualRiskModal
              open={true}
              mode="edit"
              scope="contract"
              initial={{
                category: r.category,
                severity: r.severity as 'critical' | 'major' | 'minor',
                description: r.description,
                proposed: r.proposed_change ?? '',
              }}
              onClose={() => setEditingRiskId(null)}
              onSubmit={async (v) => {
                await patchRisk({
                  contractId,
                  riskId: r.id,
                  category: v.category,
                  severity: v.severity,
                  description: v.description,
                  proposed_change: v.proposed,
                }).unwrap();
              }}
            />
          );
        })()}
      <ConfirmDialog
        open={!!deletingRiskId}
        title="Удалить риск?"
        warning="Риск будет удалён из договора и из памяти агента. Действие необратимо."
        confirmText="удалить"
        confirmLabel="Введите «удалить» для подтверждения"
        confirmButtonLabel="Удалить риск"
        busyLabel="Удаляем…"
        isBusy={false}
        onConfirm={async () => {
          if (!deletingRiskId) return;
          await deleteRisk({ contractId, riskId: deletingRiskId }).unwrap();
          setDeletingRiskId(null);
        }}
        onCancel={() => setDeletingRiskId(null)}
      />
      {viewClauseRiskId &&
        (() => {
          const r = data?.risks.find((x) => x.id === viewClauseRiskId);
          if (!r) return null;
          return (
            <RiskClausePanel
              open={true}
              title={`${CATEGORY_LABELS[r.category] ?? r.category}`}
              severity={r.severity}
              clauseQuote={r.clause_quote}
              description={r.description}
              proposed={r.proposed_change}
              evidence={r.evidence_used}
              sourceDocument={r.source_document}
              onClose={() => setViewClauseRiskId(null)}
            />
          );
        })()}
      {selectMode && (
        <RiskBulkActionBar
          selectedCount={selectedRisks.size}
          totalCount={data?.risks.length ?? 0}
          allSelected={(data?.risks.length ?? 0) > 0 && selectedRisks.size === (data?.risks.length ?? 0)}
          onToggleAll={() => {
            const all = data?.risks ?? [];
            if (selectedRisks.size === all.length) {
              setSelectedRisks(new Set());
            } else {
              setSelectedRisks(new Set(all.map((r) => r.id)));
            }
          }}
          onCancel={exitSelectMode}
          onDecide={bulkDecide}
          busy={isBulkAnnotating}
        />
      )}
    </>
  );
}

export { ContractHeaderSection } from './ContractCard/ContractHeaderSection';
export { ContractVersionsSection } from './ContractCard/ContractVersionsSection';
export { ContractRisksSection } from './ContractCard/ContractRisksSection';
export { ContractDeadlinesSection } from './ContractCard/ContractDeadlinesSection';
